import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import useRedirectIfLoggedIn from 'dashboard/lib/hooks/useRedirectIfLoggedIn';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { MfaMethod } from 'dashboard/models/user';
import { UserAuthenticationStatus } from 'dashboard/models/user-authentication';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { cancelMfa, clearAuthenticationState } from 'dashboard/redux/actions/user-authentication';
import { RootState } from 'dashboard/redux/store';
import { activateMfa, startMfa } from 'dashboard/redux/thunk-actions/user-authentication';
import DashboardLoginMfaView from 'dashboard/views/DashboardLoginMfaView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';

const DashboardLoginMfaRoute = (props: PropsFromRedux) => {
  useRedirectIfLoggedIn();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // this can happen if user refreshes page
    if (
      props.userAuthentication == null ||
      props.userAuthentication.attributes.status === UserAuthenticationStatus.Expired
    ) {
      navigate('/dashboard/login', { replace: true });
    }
  }, [props.userAuthentication]);

  const {
    callback: startMfa,
    loading: startMfaLoading,
    error: startMfaError,
  } = useApiCallback(
    async (method: MfaMethod) => {
      props.clearMessages();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.startMfa(props.userAuthentication.id, method);
    },
    [props.userAuthentication],
  );

  const { callback: resendCode, error: resendCodeError } = useApiCallback(
    async (method: MfaMethod.Email | MfaMethod.Phone) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.startMfa(props.userAuthentication.id, method);
      props.showMessage("Success. We've sent you another code.", MessageType.Success);
    },
    [props.userAuthentication],
  );

  useApiErrorHandler({
    error: startMfaError ?? resendCodeError,
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
    onError: (e) => {
      if (e.errors?.[0].title === 'Phone number unsubscribed') {
        return props.showMessage(
          'The provided number is opted out from receiving messages from us. Please try a different number.',
        );
      }
      if (e.errors?.[0].title === 'Rate limit exceeded') {
        return props.showMessage(
          'You are sending too many messages. Please try again in several minutes.',
        );
      }
      switch (e.errors?.[0].details) {
        case '2FA method not enabled':
          props.showMessage(
            'You have not enabled this 2-factor authentication method. Please try a different method.',
          );
          break;
        case 'User is locked due to too many failed authentication attempts':
          props.clearAuthenticationState();
          props.showMessage(
            'Your account has been locked for 1 hour due to too many failed authentication attempts.',
          );
          break;
        default:
          props.showMessage(
            'Sorry, something went wrong. Please try again later or contact support.',
          );
      }
    },
  });

  const { callback: activateMfa, error: activateMfaError } = useApiCallback(
    async (method: MfaMethod, code: string) => {
      props.clearMessages();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.activateMfa({ id: props.userAuthentication.id, method, code, location });
    },
    [props.userAuthentication, location],
  );

  useApiErrorHandler({
    error: activateMfaError,
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
    onError: (e) => {
      switch (e.errors?.[0].details) {
        case 'User is locked due to too many failed authentication attempts':
          props.clearAuthenticationState();
          props.showMessage(
            'Your account has been locked for 1 hour due to too many failed authentication attempts.',
          );
          break;
        case 'Incorrect credentials':
          props.showMessage('Your provided code is invalid. Please try again.');
          break;
        case '2FA code expired':
          props.showMessage('Your provided code has expired. Please try again.');
          break;
        default:
          props.showMessage(
            'Sorry, something went wrong. Please try again later or contact support.',
          );
      }
    },
  });

  return (
    <DashboardLoginMfaView
      loading={startMfaLoading}
      startMfa={startMfa}
      resendCode={resendCode}
      activateMfa={activateMfa}
      availableMfaMethods={props.userAuthentication?.attributes['available-mfa-methods'] ?? []}
      startedMfaMethod={props.startedMfaMethod}
      cancelMfa={props.cancelMfa}
      clearAuthenticationState={props.clearAuthenticationState}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  userAuthentication: state.userAuthentication.userAuthentication,
  startedMfaMethod: state.userAuthentication.startedMfaMethod,
});

const mapDispatchToProps = {
  clearMessages,
  showMessage,
  startMfa,
  activateMfa,
  cancelMfa,
  clearAuthenticationState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardLoginMfaRoute);
